import { PureComponent, createContext } from 'react';
import { PlaygroundFiles, PlaygroundFile } from '@playground/models/files.model';
import { TestsResults } from '@playground/SQL/models/tests.model';
import { RunnerState } from '@playground/models/types';
import { PyodideRuntime } from './PyodideRuntime';

interface Props {
  files: PlaygroundFiles;
  entryFile: string;
}

export interface SyntaxError {
  path: string;
  message?: string;
}

interface State {
  activeFile: string;
  entryFile: string;
  files: PlaygroundFiles;
  setFiles: (files: PlaygroundFiles) => void;
  setActiveFile: (file: string) => void;
  getVisibleFiles: () => PlaygroundFile[];
  updateCurrentFile: (newCode: string) => void;
  runCode: () => void;
  runTests: () => void;
  clearLogs: () => void;
  status: RunnerState;
  logs: string[];
  tests: TestsResults;
  readStd: boolean;
}

export const PyodideContext = createContext<State | null>(null);

class PyodideProvider extends PureComponent<Props, State> {
  private runtime: PyodideRuntime | null = null;

  constructor(props: Props) {
    super(props);
    const { files, entryFile } = props;
    this.state = {
      files: files ?? {},
      entryFile: entryFile ?? null,
      activeFile: entryFile ?? '',
      setFiles: this.setFiles,
      setActiveFile: this.setActiveFile,
      getVisibleFiles: this.getVisibleFiles,
      updateCurrentFile: this.updateCurrentFile,
      runCode: this.runCode,
      runTests: this.runTests,
      clearLogs: this.clearLogs,
      status: 'init',
      logs: [],
      readStd: false,
      tests: {
        specs: {},
        status: 'init',
      },
    };
  }

  async componentDidMount() {
    try {
      this.setState({ status: 'loading', activeFile: this.selectActiveFile() });
      this.runtime = new PyodideRuntime({
        stdout: (msg: string) => {
          this.addLogStdout(msg);
        },
        stderr: (msg: string) => {
          this.addLogStderr(msg);
        },
        files: this.state.files,
      });
      const sqlSetup = this.getSQLSetup();
      await this.runtime.initPyodide(sqlSetup);
      this.setState({ status: 'idle' });
    } catch (error) {
      console.error(error);
    }
  }

  private getSQLSetup() {
    const { files } = this.state;
    const setupFile = files['setup.sql'];
    if (!setupFile) {
      return '';
    }
    return setupFile.code;
  }

  private selectActiveFile() {
    const { entryFile } = this.props;
    if (entryFile) {
      return entryFile;
    }
    const visibleFiles = this.getVisibleFiles();
    const activeFile = visibleFiles.find((item) => item.active);
    if (activeFile) {
      return activeFile.path;
    }
    if (visibleFiles.length >= 0) {
      return visibleFiles[0].path;
    }
    return '';
  }

  updateCurrentFile = (code: string): void => {
    this.updateFile(this.state.activeFile, code);
  };

  updateFile = (pathOrFiles: string, code?: string) => {
    let files = this.state.files;

    if (typeof pathOrFiles === 'string' && code) {
      if (code === this.state.files[pathOrFiles]?.code) {
        return;
      }

      files = {
        ...files,
        [pathOrFiles]: { code, path: pathOrFiles },
      };
    }

    this.setState({ files });
  };

  runCode = async () => {
    if (this.runtime) {
      const { files, activeFile } = this.state;
      const code = files[activeFile]?.code;
      if (code) {
        this.setState({ readStd: true }, async () => {
          await this.runtime.runCode(code);
        });
      }
    }
  };

  runTests = async () => {
    if (this.runtime) {
      const { files } = this.state;
      await this.runtime.updateFiles(files);
      const testsResults = await this.runtime.runTests();
      this.setState({ tests: testsResults, logs: [] });
    }
  };

  render() {
    const { children } = this.props;
    const state = this.getState();
    return <PyodideContext.Provider value={state}>{children}</PyodideContext.Provider>;
  }

  private getState() {
    return this.state;
  }

  setFiles = async (files: PlaygroundFiles) => {
    this.setState({
      files,
      activeFile: this.selectActiveFile() ?? '',
    });
  };

  getVisibleFiles = () => {
    const { files } = this.state;
    return Object.values(files).filter((file) => !file.hidden);
  };

  setActiveFile = (filePath: string) => {
    this.setState({ activeFile: filePath });
  };

  clearLogs = () => {
    this.setState({ logs: [] });
  };

  private addLogStdout(msg: string) {
    const { readStd } = this.state;
    if (msg !== 'Python initialization complete' && readStd) {
      this.setState((prev) => ({
        ...prev,
        logs: [...prev.logs, msg],
      }));
    }
  }

  private addLogStderr(msg: string) {
    const { readStd } = this.state;
    if (readStd) {
      this.setState((prev) => ({
        ...prev,
        logs: [...prev.logs, msg],
      }));
    }
  }
}

export default PyodideProvider;
